import {
  prisma,
  type Prisma,
  ResourceType,
  ActionType,
} from "client";
import { flattenTicketFromBookableItem } from "../bookableItem/helpers";
import { getBookableItemIdsFromTicketIds } from "services/bookableItem";

export {
  getVenueZonesTabData,
  getVenueCheckInRulesTabData,
  getVenueMeetingRoomsTabData,
} from "./getVenueTabData";

// READ

export const getZone = async (userId: string, id: string) => {
  try {
    const zone = await prisma.zone.findUnique({
      where: { id },
    });
    return zone;
  } catch (error) {
    return { error };
  }
};

export const getZoneByAgentId = async (
  userId: string,
  agentId: string,
  eventId: string,
  locale?: string
) => {
  try {
    const zones = await prisma.zone.findMany({
      where: {
        doorAgents: {
          some: { id: agentId },
        },
        venue: {
          events: {
            some: {
              id: eventId,
            },
          },
        },
      },
      include: {
        translations: {
          include: { language: true },
        },
        accessibleBookableItems: {
          include: {
            translations: {
              include: {
                language: true,
              },
            },
            ticket: true,
          },
        },
        accessibleUserGroups: true,
        doorAgents: true,
        venue: {
          include: {
            translations: {
              include: { language: true },
            },
          },
        },
      },
    });
    return zones.map((zone) => {
      return {
        ...zone,
        name: zone.translations.find(
          (translation) => translation.language.code === locale
        )?.name,
        accessibleTickets: zone.accessibleBookableItems
          ?.map((bookableItem) => flattenTicketFromBookableItem(bookableItem))
          .filter(Boolean),
      };
    });
  } catch (error) {
    return { error };
  }
};

export const getZonesByEventId = async (
  userId: string,
  eventId: string,
  locale?: string,
  isDoorAgent?: boolean,
  getAllZonesForAgent?: boolean
) => {
  try {
    const zones = await prisma.zone.findMany({
      where: {
        deletedAt: null,
        ...(isDoorAgent && !getAllZonesForAgent
          ? {
              doorAgents: {
                some: {
                  id: userId,
                },
              },
            }
          : {}),
        venue: {
          events: {
            some: {
              id: eventId,
            },
          },
        },
      },
      include: {
        translations: {
          include: {
            language: true,
          },
        },

        accessibleBookableItems: {
          include: {
            translations: {
              include: {
                language: true,
              },
            },
            ticket: true,
          },
        },
        accessibleUserGroups: true,
        doorAgents: true,
        venue: {
          include: {
            translations: {
              include: { language: true },
            },
          },
        },
      },
    });
    return zones.map((zone) => {
      return {
        ...zone,
        name: zone.translations.find(
          (translation) => translation.language.code === locale
        )?.name,
        venue: {
          ...zone.venue,
          name: zone.venue?.translations.find(
            (translation) => translation.language.code === locale
          )?.name,
        },
        accessibleTickets: zone.accessibleBookableItems
          .map((bookableItem) => flattenTicketFromBookableItem(bookableItem))
          .filter(Boolean),
        accessByUser: zone.doorAgents?.some((agent) => agent.id === userId)
          ? true
          : false,
      };
    });
  } catch (error) {
    return { error };
  }
};

export const getZonesByVenueId = async (
  userId: string,
  venueId: string,
  locale?: string
) => {
  try {
    const zones = await prisma.zone.findMany({
      where: {
        deletedAt: null,
        venue: {
          id: venueId,
        },
      },
      include: {
        translations: {
          include: {
            language: true,
          },
        },
        accessibleBookableItems: {
          include: {
            translations: {
              include: {
                language: true,
              },
            },
            ticket: true,
          },
        },
        accessibleUserGroups: true,
        doorAgents: true,
        venue: {
          include: {
            translations: {
              include: { language: true },
            },
          },
        },
      },
    });

    return zones.map((zone) => {
      return {
        ...zone,
        name: zone.translations.find(
          (translation) => translation.language.code === locale
        )?.name,
        venue: {
          ...zone.venue,
          name: zone.venue?.translations.find(
            (translation) => translation.language.code === locale
          )?.name,
        },
        accessibleTickets: zone.accessibleBookableItems
          .map((bookableItem) => flattenTicketFromBookableItem(bookableItem))
          .filter(Boolean),
      };
    });
  } catch (error) {
    return { error };
  }
};

// CREATE

export const createZone = async (
  userId: string,
  zone: Prisma.ZoneCreateInput,
  translations: Prisma.ZoneTranslationsCreateManyInput[],
  ticketIds: string[],
  userGroupIds: string[],
  invitationIds: string[],
  agents: string[]
) => {
  try {
    const bookableItemIds = ticketIds.length
      ? await getBookableItemIdsFromTicketIds(ticketIds)
      : [];
    const createdZone = await prisma.zone.create({
      data: {
        ...zone,
        translations: {
          createMany: {
            data: translations,
          },
        },
        accessibleBookableItems: {
          connect: bookableItemIds.map((id) => ({ id })),
        },
        accessibleUserGroups: {
          connect: userGroupIds.map((id) => ({ id })),
        },
        accessibleInvitations: {
          connect: invitationIds.map((id) => ({ id })),
        },
        doorAgents: {
          connect: agents.map((id) => ({ id })),
        },
        deletedAt: null,
      },
    });
    return createdZone;
  } catch (error) {
    return { error };
  }
};

// UPDATE

export const updateZone = async (
  userId: string,
  id: string,
  zone: Prisma.ZoneUpdateInput,
  translations: Prisma.ZoneTranslationsCreateManyInput[],
  ticketIds: string[],
  userGroupIds: string[],
  invitationIds: string[],
  agents: string[]
) => {
  try {
    const bookableItemIds = ticketIds.length
      ? await getBookableItemIdsFromTicketIds(ticketIds)
      : [];
    const updatedZone = await prisma.zone.update({
      where: { id },
      data: {
        ...zone,
        translations: {
          deleteMany: {},
          createMany: {
            data: translations,
          },
        },
        accessibleBookableItems: {
          set: bookableItemIds.map((id) => ({ id })),
        },
        accessibleUserGroups: {
          set: userGroupIds.map((id) => ({ id })),
        },
        doorAgents: {
          set: agents.map((id) => ({ id })),
        },
        accessibleInvitations: {
          set: invitationIds.map((id) => ({ id })),
        },
      },
    });
    return updatedZone;
  } catch (error) {
    return { error };
  }
};

// DELETE

export const deleteZone = async (userId: string, id: string) => {
  try {
    const deletedZone = await prisma.zone.update({
      where: { id },
      data: {
        deletedAt: new Date(),
      },
    });
    return deletedZone;
  } catch (error) {
    return { error };
  }
};
